import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import { ADMIN } from '@/constants/testIds';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { UserPlus, KeyRound, Eye, EyeOff } from 'lucide-react';
import ResetPasswordModal from '@/components/ResetPasswordModal';

const ROLE_LABELS = { admin: 'Administrador', inspector: 'Auditor', client: 'Cliente', provider: 'Proveedor' };

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', company: '', password: '', role: 'inspector' });
    const [resetTarget, setResetTarget] = useState(null);
    const [showPw, setShowPw] = useState(false);

    const load = () => api.get('/admin/users').then((r) => setUsers(r.data));

    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post('/admin/users', form);
            toast.success('Usuario creado');
            setForm({ name: '', email: '', company: '', password: '', role: 'inspector' });
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error'));
        }
    };

    const toggleActive = async (u) => {
        await api.patch(`/admin/users/${u.id}/active?active=${!u.active}`);
        load();
    };

    return (
        <AdminLayout title="Gestión de usuarios">
            <PageHeader title="Gestión de usuarios" subtitle="◆ Administrador" accent="#7C6BEF">
                <button
                    onClick={() => setShowForm(!showForm)}
                    data-testid={ADMIN.createUserButton}
                    className="bg-[#111111] text-white px-5 py-3 font-medium hover:bg-black inline-flex items-center gap-2 transition-colors"
                >
                    <UserPlus className="w-4 h-4" /> Crear usuario
                </button>
            </PageHeader>

            {showForm && (
                <form onSubmit={create} className="border border-gray-200 p-6 mb-6 bg-white grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-medium block mb-1">Nombre</label>
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full border border-gray-300 px-3 py-2 focus:border-black outline-none" />
                    </div>
                    <div>
                        <label className="text-sm font-medium block mb-1">Correo</label>
                        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full border border-gray-300 px-3 py-2 focus:border-black outline-none" />
                    </div>
                    <div>
                        <label className="text-sm font-medium block mb-1">Empresa</label>
                        <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                            className="w-full border border-gray-300 px-3 py-2 focus:border-black outline-none" />
                    </div>
                    <div>
                        <label className="text-sm font-medium block mb-1">Rol</label>
                        <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="w-full border border-gray-300 px-3 py-2 bg-white focus:border-black outline-none">
                            <option value="inspector">Auditor</option>
                            <option value="client">Cliente</option>
                            <option value="admin">Administrador</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-sm font-medium block mb-1">Contraseña</label>
                        <div className="relative">
                            <input type={showPw ? 'text' : 'password'} required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="w-full border border-gray-300 px-3 py-2 pr-10 focus:border-black outline-none" />
                            <button type="button" onClick={() => setShowPw((v) => !v)}
                                aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                title={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 focus:outline-none">
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="md:col-span-2 flex gap-2">
                        <button type="submit" className="bg-[#111111] text-white px-5 py-2 font-medium hover:bg-black">Crear</button>
                        <button type="button" onClick={() => setShowForm(false)} className="border border-gray-300 px-5 py-2 hover:bg-gray-50">Cancelar</button>
                    </div>
                </form>
            )}

            <div className="border border-gray-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="text-left px-4 py-3 font-medium">Nombre</th>
                            <th className="text-left px-4 py-3 font-medium">Correo</th>
                            <th className="text-left px-4 py-3 font-medium">Rol</th>
                            <th className="text-left px-4 py-3 font-medium">Empresa</th>
                            <th className="text-left px-4 py-3 font-medium">Estado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium">{u.name}</td>
                                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                                <td className="px-4 py-3">
                                    <span className="text-[10px] uppercase tracking-wider border border-gray-300 px-2 py-1 font-medium">
                                        {ROLE_LABELS[u.role]}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600">{u.company || '—'}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs font-medium ${u.active ? 'text-[#00875A]' : 'text-[#E53935]'}`}>
                                        {u.active ? '● Activo' : '○ Inactivo'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => setResetTarget(u)}
                                        data-testid={`reset-password-btn-${u.id}`}
                                        className="text-xs inline-flex items-center gap-1 mr-3 hover:underline" title="Resetear contraseña">
                                        <KeyRound className="w-3 h-3" /> Resetear
                                    </button>
                                    <button onClick={() => toggleActive(u)} className="text-xs underline">
                                        {u.active ? 'Desactivar' : 'Activar'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {resetTarget && (
                <ResetPasswordModal
                    userId={resetTarget.id}
                    userLabel={resetTarget.name || resetTarget.email}
                    onClose={() => setResetTarget(null)}
                />
            )}
        </AdminLayout>
    );
}
